import { cloneElement, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn } from 'lucide-react';

/**
 * ChartZoom — click-to-enlarge wrapper for interactive charts.
 * Ported from raisy_FE/src/components/blog/ChartZoom.tsx unchanged (same
 * lightbox idiom as BlogImage: portal, Esc / backdrop-click close, body
 * overflow lock, opaque backdrop).
 */
export const ChartZoom = ({ children }: { children: React.ReactElement }) => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, close]);

  return (
    <>
      <div
        className="relative group cursor-zoom-in"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
        aria-label="Enlarge chart"
      >
        {children}
        <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <ZoomIn className="w-3.5 h-3.5" /> Enlarge
        </span>
      </div>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged chart"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 text-white/90 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <div
              className="w-[min(1100px,94vw)] bg-[#171717] rounded-xl p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {cloneElement(children as React.ReactElement<{ height?: number }>, { height: 540 })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
